import { Component, OnInit, OnDestroy, Input, signal, computed, effect, resource, OnChanges, SimpleChanges } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { AppService } from "@App/app.service";
import { ObjectTagsComponent } from "@Components/object-tags/object-tags.component";
import { AccountSettingsDTO } from "@DTOs";
import { AccountSettings } from "@Enums";
import { Client, Account, UWResponse } from "@Models";
import { AccountSettingsDataService } from "@Services";
import { SharedModule } from "@Shared/shared.module";
import { TimeUtils, Utils } from "@Utils";
import { catchError, map, Observable, of } from "rxjs";
import { SubSink } from "subsink";

@Component({
    selector: 'workbench-document-exclusion-config',
    templateUrl: './workbench-document-exclusion-config.component.html',    
    imports: [SharedModule, ObjectTagsComponent]
})
export default class WorkbenchDocumentExclusionComponent implements OnInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;
    
    documentExclusionForm: UntypedFormGroup;
    accountSettings: AccountSettingsDTO[] = [];
    accountSettingsLoaded = false;
    lastUpdated: Date;
    orphanLastUpdated: Date;
    errorMessage = '';
    
    attachmentTags: string[] = [];
    orphanAttachmentTags: string[] = [];
    subs = new SubSink();

    constructor(
        private appService: AppService,
        private accountSettingsDataService: AccountSettingsDataService,
    ) {
    }

    ngOnInit() {
        this.subscribeToAccountSettings();
    }

    saveRequirementTags(tags: string[]) {  
        this.attachmentTags = tags;
        this.save(AccountSettings.requirementTagExclusions).subscribe();
    }

    saveOrphanTags(tags: string[]) {  
        this.attachmentTags = tags;
        this.save(AccountSettings.orphanRequirementTagExclusions).subscribe();
    }

    subscribeToAccountSettings() {
        this.subs.add(this.accountSettingsDataService.loadAccountSettings(this.account.id).subscribe(accountSettings => {
            this.accountSettings = accountSettings;
            this.setForm();
            this.accountSettingsLoaded = true;
        }));
    }

    getAccountSetting(accountSetting: AccountSettings) {
        const exclusionSetting = this.accountSettings?.find(x => x.name === accountSetting);

        if (!exclusionSetting) {
            return null;
        }
        return exclusionSetting;
    }
    
    setForm() {
        const requirementExclusionSetting = this.getAccountSetting(AccountSettings.requirementTagExclusions);
        const orphanRequirementExclusionSetting = this.getAccountSetting(AccountSettings.orphanRequirementTagExclusions);

        this.attachmentTags = (requirementExclusionSetting?.value ? requirementExclusionSetting.value.split(',') : []);
        this.orphanAttachmentTags = (orphanRequirementExclusionSetting?.value ? orphanRequirementExclusionSetting.value.split(',') : []);

        this.lastUpdated = TimeUtils.convertToDateCst(requirementExclusionSetting?.lastModifiedDate ?? requirementExclusionSetting?.creationDate);
        this.orphanLastUpdated = TimeUtils.convertToDateCst(orphanRequirementExclusionSetting?.lastModifiedDate ?? orphanRequirementExclusionSetting?.creationDate);
    }

    save(accountSetting: AccountSettings) {
        const existingAccountSetting = this.accountSettings.find(x => x.name === accountSetting);

        const updateAccountSetting: AccountSettingsDTO = {
            'id': existingAccountSetting?.id ?? Utils.emptyGuid,
            'accountId': this.account.id,
            'name': accountSetting,
            'value': this.attachmentTags.join(',')
        };

        const saveSettings$ = this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map(_response => {
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();

                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                return of(false);
            }),
        );

        return saveSettings$;
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }
}